/**
 * Auth context for the Insights SPA.
 *
 * On mount it calls `/api/auth/me` to discover the current session. Any 401
 * from a data call (registered via setUnauthorizedHandler) flips the app back
 * to the anonymous state so the login screen is shown. When the backend runs
 * with auth disabled (`INSIGHTS_AUTH_DISABLED=1`) the app renders normally
 * without a login step.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { setUnauthorizedHandler } from './api';
import {
  fetchMe,
  login as loginRequest,
  logout as logoutRequest,
  type AuthUser,
  type MeResponse,
} from './authApi';

type AuthStatus = 'loading' | 'authed' | 'anon';

type AuthState = {
  status: AuthStatus;
  authDisabled: boolean;
  user: AuthUser | null;
  restaurantIds: string[];
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

function applyMe(
  me: MeResponse,
  set: (partial: Partial<AuthState>) => void,
): void {
  set({
    status: 'authed',
    authDisabled: me.authDisabled,
    user: me.user,
    restaurantIds: me.restaurants.map((r) => r.id),
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [authDisabled, setAuthDisabled] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [restaurantIds, setRestaurantIds] = useState<string[]>([]);

  const set = useCallback((partial: Partial<AuthState>) => {
    if (partial.status !== undefined) setStatus(partial.status);
    if (partial.authDisabled !== undefined) setAuthDisabled(partial.authDisabled);
    if (partial.user !== undefined) setUser(partial.user);
    if (partial.restaurantIds !== undefined) setRestaurantIds(partial.restaurantIds);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        if (me) {
          applyMe(me, set);
        } else {
          setStatus('anon');
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('anon');
      });
    return () => {
      cancelled = true;
    };
  }, [set]);

  // A 401 from any data call means the session is gone -> show login.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setRestaurantIds([]);
      setStatus('anon');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const me = await loginRequest(email, password);
      applyMe(me, set);
    },
    [set],
  );

  const logout = useCallback(async () => {
    await logoutRequest();
    setUser(null);
    setRestaurantIds([]);
    setStatus('anon');
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, authDisabled, user, restaurantIds, login, logout }),
    [status, authDisabled, user, restaurantIds, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
